import { Routes, Route, Navigate, NavLink, useNavigate } from 'react-router-dom';
import {
  AppShell,
  Burger,
  Badge,
  Group,
  Text,
  NavLink as MantineNavLink,
  Stack,
  UnstyledButton,
  Button,
  Divider,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { useQuery } from '@tanstack/react-query';
import { Dashboard } from './pages/Dashboard';
import { ItemDetail } from './pages/ItemDetail';
import { CreateItem } from './pages/CreateItem';
import { LostClaimForm } from './pages/LostClaimForm';
import { LostClaimsList } from './pages/LostClaimsList';
import { OldItems } from './pages/OldItems';
import { LostClaimDetail } from './pages/LostClaimDetail';
import { Matches } from './pages/Matches';
import { MatchSettings } from './pages/MatchSettings';
import { Settings } from './pages/Settings';
import { Login } from './pages/Login';
import { getToken, clearToken } from './api/client';
import { suggestionsApi } from './api/suggestions';

function RequireAuth({ children }: { children: React.ReactNode }) {
  if (!getToken()) {
    return <Navigate to="/login" replace />;
  }
  return <>{children}</>;
}

function AppLayout() {
  const [opened, { toggle, close }] = useDisclosure(false);
  const navigate = useNavigate();

  const { data: countData } = useQuery({
    queryKey: ['suggestions', 'count'],
    queryFn: suggestionsApi.count,
  });
  const pendingCount = countData?.count ?? 0;

  function handleLogout() {
    clearToken();
    navigate('/login', { replace: true });
  }

  return (
    <AppShell
      header={{ height: 56 }}
      navbar={{ width: 240, breakpoint: 'sm', collapsed: { mobile: !opened } }}
      padding="md"
    >
      <AppShell.Header>
        <Group h="100%" px="md" justify="space-between">
          <Group>
            <Burger opened={opened} onClick={toggle} hiddenFrom="sm" size="sm" />
            <UnstyledButton onClick={() => navigate('/')}>
              <Text fw={700} size="lg">
                KK Glemte Sager
              </Text>
            </UnstyledButton>
          </Group>
          <Button variant="subtle" size="xs" color="gray" onClick={handleLogout}>
            Log ud
          </Button>
        </Group>
      </AppShell.Header>

      <AppShell.Navbar p="md">
        <Stack gap="xs">
          <MantineNavLink component={NavLink} to="/" label="Fundne genstande" onClick={close} />
          <MantineNavLink
            component={NavLink}
            to="/items/new"
            label="Registrer genstand"
            onClick={close}
          />
          <MantineNavLink
            component={NavLink}
            to="/items/old"
            label="Gamle genstande"
            onClick={close}
          />
          <MantineNavLink
            component={NavLink}
            to="/lost-claims"
            label="Efterlysninger"
            onClick={close}
          />
          <MantineNavLink
            component={NavLink}
            to="/lost-claims/new"
            label="Ny efterlysning"
            onClick={close}
          />
          <MantineNavLink
            component={NavLink}
            to="/matches"
            label={
              <Group gap="xs" justify="space-between" wrap="nowrap">
                <span>Mulige match</span>
                {pendingCount > 0 && (
                  <Badge size="sm" color="red" variant="filled" circle>
                    {pendingCount}
                  </Badge>
                )}
              </Group>
            }
            onClick={close}
          />
          <Divider mt="xs" />
          <Text size="xs" fw={600} tt="uppercase" c="dimmed" px={4}>Indstillinger</Text>
          <MantineNavLink
            component={NavLink}
            to="/settings"
            label="Kategorier & metadata"
            onClick={close}
          />
        </Stack>
      </AppShell.Navbar>

      <AppShell.Main>
        <Routes>
          <Route path="/" element={<Dashboard />} />
          <Route path="/items/new" element={<CreateItem />} />
          <Route path="/items/old" element={<OldItems />} />
          <Route path="/items/:id" element={<ItemDetail />} />
          <Route path="/lost-claims" element={<LostClaimsList />} />
          <Route path="/lost-claims/new" element={<LostClaimForm />} />
          <Route path="/lost-claims/:id" element={<LostClaimDetail />} />
          <Route path="/matches" element={<Matches />} />
          <Route path="/matches/settings" element={<MatchSettings />} />
          <Route path="/settings" element={<Settings />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </AppShell.Main>
    </AppShell>
  );
}

export function App() {
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route
        path="/*"
        element={
          <RequireAuth>
            <AppLayout />
          </RequireAuth>
        }
      />
    </Routes>
  );
}
